import axios, { AxiosInstance } from 'axios';
import { APIResponse } from '@shared/types';

const TOKEN_KEY = 'zaphub_token';
const REFRESH_TOKEN_KEY = 'zaphub_refresh_token';

class APIClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshQueue: Array<(token: string) => void> = [];

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3004/api',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Attach token to every request
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle 401 — try refresh token before redirecting
    this.client.interceptors.response.use(
      (response) => response.data,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

          if (refreshToken && !this.isRefreshing) {
            originalRequest._retry = true;
            this.isRefreshing = true;

            try {
              const res = await axios.post(
                `${this.client.defaults.baseURL}/auth/refresh`,
                { refreshToken }
              );
              const newToken = res.data?.data?.accessToken;
              if (newToken) {
                localStorage.setItem(TOKEN_KEY, newToken);
                this.refreshQueue.forEach(cb => cb(newToken));
                this.refreshQueue = [];
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return this.client(originalRequest);
              }
            } catch {
              // Refresh failed — clear session and redirect
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
              localStorage.removeItem('zaphub_user');
              window.location.href = '/login';
            } finally {
              this.isRefreshing = false;
            }
          } else if (refreshToken && this.isRefreshing) {
            // Queue requests while refreshing
            return new Promise((resolve) => {
              this.refreshQueue.push((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.client(originalRequest));
              });
            });
          } else {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            localStorage.removeItem('zaphub_user');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error.response?.data || error);
      }
    );
  }

  async get<T = any>(url: string, config?: any): Promise<APIResponse<T>> {
    return this.client.get(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<APIResponse<T>> {
    return this.client.post(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<APIResponse<T>> {
    return this.client.put(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<APIResponse<T>> {
    return this.client.patch(url, data, config);
  }

  async delete<T = any>(url: string, config?: any): Promise<APIResponse<T>> {
    return this.client.delete(url, config);
  }
}

export const apiClient = new APIClient();
export default apiClient;
